// useInstallPrompt — Day 13 PR-C (D11+D12): same logic as user app.
// See apps/user/src/hooks/useInstallPrompt.ts for the rationale.
// Duplicated rather than shared because admin app rarely needs the same
// fixes synchronously and the divergence cost is tiny.

import { useEffect, useState } from "react";

export type InstallState =
  | "not_needed"
  | "ios_add"
  | "android_native"
  | "android_manual";

export interface InstallPromptState {
  state: InstallState;
  triggerNative?: () => Promise<void>;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function detectInitial(): InstallState {
  if (typeof window === "undefined") return "not_needed";

  const preview = new URLSearchParams(window.location.search).get("preview") === "true";
  if (preview) return "not_needed";

  const ua = navigator.userAgent;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
  if (!isMobile) return "not_needed";

  if ((navigator as { standalone?: boolean }).standalone === true) return "not_needed";
  if (window.matchMedia("(display-mode: standalone)").matches) return "not_needed";

  if (/iPhone|iPad|iPod/i.test(ua)) return "ios_add";
  return "android_manual";
}

export function useInstallPrompt(): InstallPromptState {
  const [state, setState] = useState<InstallState>(() => detectInitial());
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (state === "not_needed") return;

    function onBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setState("android_native");
    }
    function onAppInstalled() {
      setDeferred(null);
      setState("not_needed");
    }
    function onDisplayChange() {
      if (window.matchMedia("(display-mode: standalone)").matches) {
        setState("not_needed");
      }
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    const mq = window.matchMedia("(display-mode: standalone)");
    mq.addEventListener?.("change", onDisplayChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
      mq.removeEventListener?.("change", onDisplayChange);
    };
  }, [state]);

  const triggerNative = deferred
    ? async () => {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice.outcome === "accepted") {
          setDeferred(null);
        }
      }
    : undefined;

  return { state, triggerNative };
}
